import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  FolderIcon, 
  TicketIcon,
  CalendarIcon,
  ChartBarIcon,
  UsersIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Projects', href: '/projects', icon: FolderIcon },
  { name: 'Issues', href: '/issues', icon: TicketIcon },
  { name: 'Sprints', href: '/sprints', icon: CalendarIcon },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

const Sidebar = ({ onClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();

  const handleNavigation = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024 && onClose) {
      onClose();
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 h-full flex flex-col ${
      collapsed ? 'w-16 lg:w-20' : 'w-64'
    }`}>
      {/* Mobile close button */}
      <div className="lg:hidden flex justify-end p-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-col h-full">
        {/* Banner Area */}
        <div className="bg-black h-20 flex items-center justify-center overflow-hidden">
          {collapsed ? (
            logoError ? (
              <div className="text-white text-xs font-bold">JT</div>
            ) : (
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="h-8 w-8 object-contain"
                onError={() => setLogoError(true)}
              />
            )
          ) : logoError ? (
            <div className="text-white text-center">
              <div className="text-lg font-bold">Jira Tracker</div>
              <div className="text-xs text-gray-400 mt-1">Project Management</div>
            </div>
          ) : (
            <img 
              src="/logo.png" 
              alt="Company Banner" 
              className="h-16 object-contain"
              onError={() => setLogoError(true)}
            />
          )}
        </div>

        <div className="flex-1 px-3 lg:px-4 py-4 lg:py-6">
          <nav className="space-y-1 lg:space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavigation}
                  className={`group flex items-center px-2 lg:px-3 py-2 lg:py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <item.icon className={`flex-shrink-0 h-5 w-5 ${
                    collapsed ? 'mx-auto' : 'mr-3'
                  }`} />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </nav>
        </div>
        
        {/* Collapse button - hidden on mobile */}
        <div className="p-3 lg:p-4 border-t border-gray-200 dark:border-gray-700 hidden lg:block">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
          >
            {collapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeftIcon className="h-5 w-5 mr-2" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;